import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { validarCampos } from "./validators";
import { getSectorNameByValvulaId, construirValvulasConDuracion } from "./utils";
import { useProgramaciones } from "./useProgramaciones";
import ProgramacionList from "./ProgramacionList";
import ProgramacionModal from "./ProgramacionModal";
import HistorialModal from "./HistorialModal";
import { mostrarMensaje } from "../../../src/utils/Mensaje";

export default function Programacion() {
  const { moduloId } = useParams();
  const location = useLocation();
  const equipoRaw = location.state?.equipo || localStorage.getItem("equipoActivo");
  const equipo = typeof equipoRaw === "string" ? JSON.parse(equipoRaw) : equipoRaw;
  const sectores = equipo?.sectores ?? [];

  const {
    programaciones,
    historial,
    mensaje,
    loading,
    error,
    agregarProgramacion,
    editarProgramacion,
    borrarProgramacion,
  } = useProgramaciones(moduloId);

  // Mostrar mensajes y errores
  useEffect(() => {
    if (mensaje) mostrarMensaje(mensaje, "success", 2000);
  }, [mensaje]);

  useEffect(() => {
    if (error) console.error("Error al cargar programaciones:", error);
  }, [error]);

  // UI state
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [tipoNuevo, setTipoNuevo] = useState("semanal");
  const [nuevoProg, setNuevoProg] = useState({
    startTime: "",
    cyclesPerDay: 1,
    intervalTime: "",
    days: [],
    startDate: "",
    daysInterval: 1,
    cyclicStartTime: "",
    cyclicCyclesPerDay: 1,
    cyclicIntervalTime: "",
  });
  const [sectoresSeleccionados, setSectoresSeleccionados] = useState([]);
  const [duracionComun, setDuracionComun] = useState("");
  const [touched, setTouched] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [editingProg, setEditingProg] = useState(null);

  // Validación
  const errors = validarCampos({ tipoNuevo, nuevoProg, sectoresSeleccionados, duracionComun });
  const esValido = Object.keys(errors).length === 0;

  // Abrir modal nuevo
  const openNuevo = () => {
    setEditingProg(null);
    setTipoNuevo("semanal");
    setNuevoProg({
      startTime: "", cyclesPerDay: 1, intervalTime: "", days: [],
      startDate: "", daysInterval: 1, cyclicStartTime: "",
      cyclicCyclesPerDay: 1, cyclicIntervalTime: "",
    });
    setSectoresSeleccionados([]);
    setDuracionComun("");
    setTouched({});
    setMostrarModal(true);
  };

  // Abrir modal editar
  const openEditar = (prog) => {
    setEditingProg(prog);
    const { type, schedule, valves } = prog.configuracion.parameters;
    setTipoNuevo(type);
    setNuevoProg({
      startTime: schedule.weekly.startTime,
      cyclesPerDay: schedule.weekly.cyclesPerDay,
      intervalTime: schedule.weekly.intervalTime,
      days: schedule.weekly.days,
      startDate: schedule.cyclic.startDate.split('T')[0],
      daysInterval: schedule.cyclic.daysInterval,
      cyclicStartTime: schedule.cyclic.startTime,
      cyclicCyclesPerDay: schedule.cyclic.cyclesPerDay,
      cyclicIntervalTime: schedule.cyclic.intervalTime,
    });
    const sel = valves.map((v) => {
      const sec = sectores.find(s =>
        s.valvulas.some(x => String(x.valvulaId) === String(v.valvulaId))
      );
      if (!sec) return null;
      return {
        sectorId: sec.sectorId,
        sectorNombre: sec.sectorNombre,
        valves: [{ valvulaId: String(v.valvulaId) }],
        duracionComunSector: v.duration,
      };
    }).filter(Boolean);
    setSectoresSeleccionados(sel);
    setDuracionComun("");
    setTouched({});
    setMostrarModal(true);
  };

  // Handlers
  const onTipoChange = setTipoNuevo;
  const onChangeField = (field, value) => {
    if (field === 'toggleDay') {
      setNuevoProg(prev => ({
        ...prev,
        days: prev.days.includes(value)
          ? prev.days.filter(d => d !== value)
          : [...prev.days, value]
      }));
      setTouched(t => ({ ...t, days: true }));
    } else {
      setNuevoProg(prev => ({ ...prev, [field]: value }));
      setTouched(t => ({ ...t, [field]: true }));
    }
  };
  const onToggleSector = sector => {
    setSectoresSeleccionados(prev => {
      const exists = prev.some(s => s.sectorId === sector.sectorId);
      if (exists) return prev.filter(s => s.sectorId !== sector.sectorId);
      return [...prev, { sectorId: sector.sectorId, sectorNombre: sector.sectorNombre,
        valves: sector.valvulas.map(v => ({ valvulaId: String(v.valvulaId) })),
        duracionComunSector: '' }];
    });
  };
  const handleDuracionSectorChange = (sectorId, val) => {
    setSectoresSeleccionados(prev => prev.map(s => s.sectorId === sectorId ? { ...s, duracionComunSector: val } : s));
  };
  const onDuracionComunChange = val => {
    setDuracionComun(val);
    setTouched(t => ({ ...t, duracionComun: true }));
  };

  const handleSave = async () => {
    if (!esValido) return;
    setGuardando(true);
    const valves = construirValvulasConDuracion(sectoresSeleccionados, duracionComun)
      .map(v => ({ valvulaId: String(v.valvulaId), duration: v.duration }));
    const data = {
      programacionId: editingProg?.programacionId || 0,
      moduloId: +moduloId,
      estadoId: 1,
      fechaCreacion: new Date().toISOString(),
      fechaActualizacion: new Date().toISOString(),
      configuracion: { clientId:'cliente-app', command:'UPDATE_SCHEDULE', parameters:{ type:tipoNuevo,
        schedule:{ weekly: { days: nuevoProg.days.length? nuevoProg.days:[1,2,3], startTime: nuevoProg.startTime, cyclesPerDay:+nuevoProg.cyclesPerDay, intervalTime: nuevoProg.intervalTime },
                   cyclic: { startDate: nuevoProg.startDate||new Date().toISOString(), daysInterval:+nuevoProg.daysInterval, startTime: nuevoProg.cyclicStartTime, cyclesPerDay:+nuevoProg.cyclicCyclesPerDay, intervalTime: nuevoProg.cyclicIntervalTime }} , valves }
      },
    };
    try { if (editingProg) await editarProgramacion(data); else await agregarProgramacion(data); setMostrarModal(false); }
    catch(err){ console.error('Error guardando:', err);} 
    finally{ setGuardando(false); setEditingProg(null); }
  };

  const handleDelete = async prog => {
    const nombres = prog.configuracion.parameters.valves.map(v => v.valvulaId);
    await borrarProgramacion(prog, nombres);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0C2D57] to-[#19547B] text-white">
      <header className="px-8 pt-6 flex justify-between items-center">
        <h1 className="text-4xl font-extrabold">DIMALINK</h1>
        <span className="text-sm uppercase bg-green-600/20 px-3 py-1 rounded-full">Conectado</span>
      </header>
      <main className="px-8 py-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center mt-8 text-white">Cargando...</div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl">Programaciones módulo #{moduloId}</h2>
              <div className="flex gap-2">
                <button onClick={()=>setMostrarHistorial(true)} className="bg-blue-800 px-4 py-2 rounded-full">Historial</button>
                <button onClick={openNuevo} className="bg-green-600 px-4 py-2 rounded-full">+ Nueva</button>
              </div>
            </div>
            {programaciones.length === 0 ? (
              <div className="text-center mt-8 text-white">No existen programaciones aún para este equipo.</div>
            ) : (
              <ProgramacionList items={programaciones} getSectorName={id=>getSectorNameByValvulaId(id,sectores)} onEdit={openEditar} onDelete={handleDelete} />
            )}
            {mostrarModal && <ProgramacionModal tipoNuevo={tipoNuevo} onTipoChange={onTipoChange} nuevoProg={nuevoProg} onChangeField={onChangeField} touched={touched} errors={errors} sectores={sectores} sectoresSeleccionados={sectoresSeleccionados} onToggleSector={onToggleSector} handleDuracionSectorChange={handleDuracionSectorChange} duracionComun={duracionComun} onDuracionComunChange={onDuracionComunChange} onSave={handleSave} onClose={()=>setMostrarModal(false)} disabledSave={guardando} isEditing={!!editingProg} />}
            {mostrarHistorial && <HistorialModal historial={historial} onClose={()=>setMostrarHistorial(false)} />}
          </>
        )}
      </main>
    </div>
  );
}
